import React, { useState, useCallback, useEffect } from 'react';
import { useAppSelector, store } from '../../store';
import {
  ExclamationTriangleIcon,
  WifiIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import connectionMonitor from '../../services/connectionMonitor';
import { getAllDraftKeys } from '../../hooks/useDraftAutosave';

const ConnectionStatusBanner: React.FC = () => {
  const connection = useAppSelector(state => state.connection);
  const auth = useAppSelector(state => state.auth);
  const [saveConfirmation, setSaveConfirmation] = useState<string | null>(null);

  // Check for full authentication state
  const isFullyAuthenticated = auth.isAuthenticated && auth.token && auth.user;

  const handleSaveClick = useCallback(() => {
    const userId = store.getState().auth.user?.id;
    if (userId) {
      const draftKeys = getAllDraftKeys(userId);
      const count = draftKeys.length;
      const msg = count > 0
        ? `${count} draft(s) auto-saved`
        : 'No unsaved drafts';
      setSaveConfirmation(msg);
      setTimeout(() => setSaveConfirmation(null), 3000);
    }
  }, []);

  useEffect(() => {
    if (isFullyAuthenticated) {
      // Start monitoring when user is fully authenticated with token and user data
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[CONNECTION] Starting monitor - user authenticated');
      }
      setTimeout(() => connectionMonitor.start(), 1000); // 1 second delay to allow session to establish
    } else {
      // Stop monitoring when user logs out or auth state is incomplete
      connectionMonitor.stop();
    }

    return () => {
      // Cleanup on unmount
      connectionMonitor.stop();
    };
  }, [isFullyAuthenticated]);

  // Don't show banner if connected or not fully authenticated
  if (connection.isConnected || !isFullyAuthenticated) {
    return null;
  }

  const getStatusMessage = () => {
    if (connection.isReconnecting) {
      return `Attempting to reconnect... (Attempt ${connection.reconnectAttempts}/12)`;
    }
    
    if (connection.countdownSeconds !== null && connection.countdownSeconds > 0) {
      const minutes = Math.floor(connection.countdownSeconds / 60);
      const seconds = connection.countdownSeconds % 60;
      
      if (minutes > 0) {
        return `Connection lost. You will be logged out in ${minutes}:${seconds.toString().padStart(2, '0')} for data safety.`;
      } else {
        return `Connection lost. You will be logged out in ${seconds} seconds for data safety.`;
      }
    }
    
    return 'Connection to server lost. Attempting to reconnect...';
  };

  const getBannerColor = () => {
    if (connection.countdownSeconds !== null && connection.countdownSeconds <= 10) {
      return 'bg-red-600'; // Critical - about to logout
    }
    if (connection.countdownSeconds !== null && connection.countdownSeconds <= 30) {
      return 'bg-orange-600'; // Warning - time running out
    }
    return 'bg-yellow-600'; // Alert - connection lost
  };

  const getIcon = () => {
    if (connection.isReconnecting) {
      return <ArrowPathIcon className="h-5 w-5 text-white animate-spin" />;
    }
    if (connection.countdownSeconds !== null && connection.countdownSeconds <= 10) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-white animate-pulse" />;
    }
    return <WifiIcon className="h-5 w-5 text-white" />;
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${getBannerColor()} text-white shadow-md transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-2">
            {getIcon()}
            <span className="font-medium text-xs sm:text-sm">
              {getStatusMessage()}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {saveConfirmation && (
              <span className="text-xs text-white bg-white bg-opacity-20 px-2 py-0.5 rounded">
                {saveConfirmation}
              </span>
            )}
            {connection.countdownSeconds !== null && connection.countdownSeconds > 30 && (
              <button
                onClick={handleSaveClick}
                className="px-2 py-0.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium transition-colors"
              >
                Save
              </button>
            )}
            
            {connection.isReconnecting && (
              <button
                onClick={() => window.location.reload()}
                className="px-2 py-0.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium transition-colors"
              >
                Refresh
              </button>
            )}
          </div>
        </div>
        
        {/* Compact progress bar for countdown */}
        {connection.countdownSeconds !== null && (
          <div className="h-0.5 bg-white bg-opacity-20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-1000 ease-linear"
              style={{ 
                width: `${(connection.countdownSeconds / 60) * 100}%`,
                backgroundColor: connection.countdownSeconds <= 10 ? '#ef4444' : '#ffffff'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatusBanner;